import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Botón flotante "Descargar App" que dispara el prompt nativo de instalación
 * (Chrome/Edge/Android + iOS Safari con instrucciones manuales).
 * - Se oculta cuando la app ya está instalada (matchMedia standalone) o el
 *   usuario descartó la invitación en esta sesión.
 * - Debe verse discreto y ubicarse por encima del BottomNavigation para no
 *   tapar productos ni el carrito.
 */
const DISMISS_KEY = 'bebida-bacana-pwa-dismissed';

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator?.standalone === true;
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (installed) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [installed]);

  const handleClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === 'accepted') setInstalled(true);
      } catch { /* usuario canceló */ }
      setDeferredPrompt(null);
      return;
    }
    if (isIOS()) {
      setShowIosHint(true);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  // Solo mostrar si hay prompt disponible (Chrome/Android/Edge) o si es iOS
  // (donde no existe beforeinstallprompt pero podemos mostrar instrucciones).
  const canInstall = !!deferredPrompt || isIOS();
  if (installed || dismissed || !canInstall) return null;

  return (
    <>
      <div
        className="fixed z-[55] right-3 bottom-20 sm:bottom-24 sm:right-6 animate-[pwaFadeIn_320ms_ease-out]"
        data-testid="pwa-install-container"
      >
        <button
          type="button"
          onClick={handleClick}
          className="group relative flex items-center gap-2 pl-3 pr-3.5 py-2.5 rounded-full bg-slate-900 text-white shadow-2xl hover:bg-slate-800 transition-all hover:scale-[1.03] active:scale-95 border border-slate-700"
          data-testid="pwa-install-button"
          aria-label="Descargar app"
        >
          <Download size={16} className="text-emerald-300" />
          <span className="text-xs font-bold tracking-wide whitespace-nowrap">Descargar App</span>
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white text-slate-500 shadow border border-slate-200 flex items-center justify-center hover:bg-slate-100"
          data-testid="pwa-install-dismiss"
          aria-label="Descartar"
        >
          <X size={11} strokeWidth={2.4} />
        </button>
      </div>

      {showIosHint && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-[pwaFadeIn_240ms_ease-out]"
          onClick={() => setShowIosHint(false)}
          data-testid="pwa-ios-hint"
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900 text-base">Instalar en iPhone/iPad</h3>
              <button
                onClick={() => setShowIosHint(false)}
                className="p-1 rounded-md hover:bg-slate-100"
                data-testid="pwa-ios-hint-close"
              >
                <X size={18} className="text-slate-500" />
              </button>
            </div>
            <ol className="text-sm text-slate-700 space-y-2 leading-snug list-decimal pl-5">
              <li>Tocá el botón <b>Compartir</b> <span aria-label="share" className="inline-block">⎋</span> en la barra inferior de Safari.</li>
              <li>Elegí <b>Agregar a Inicio</b>.</li>
              <li>Confirmá tocando <b>Agregar</b>. La app queda como un ícono en tu pantalla.</li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;
